import { anyRunning } from '../actions/log';

import { SAVE } from './edits';

import type { Action } from '../actions/log';

/**
 * Where the reviewer's work stands, at all times — GEN-55.
 *
 * `EditableText` saved and the screen looked the same afterwards, so a save that worked and
 * one that was refused were indistinguishable. The refusal was already on the action feed,
 * but the feed answers "what has the console done lately" and this answers "is my work
 * safe" — the question somebody about to close the tab is actually asking.
 */

export interface SaveNote {
  message: string;
  severity: 'success' | 'info' | 'warning' | 'error';
}

const plural = (count: number): string =>
  count === 1 ? '1 unsaved change' : `${String(count)} unsaved changes`;

/**
 * A refusal outranks unsaved text, because the two always co-occur: a refused save leaves
 * the typed value in the field, which is by definition dirty, and "3 unsaved changes" for a
 * write the API has already rejected reads as though leaving the field would fix it.
 */
export function saveNote(
  actions: readonly Action[],
  unsaved: number,
): SaveNote {
  const saves = actions.filter((action) => action.name === SAVE);

  if (anyRunning(saves)) return { message: 'Saving…', severity: 'info' };

  // `reduceActions` keeps the log newest first, so the head is the last save to settle.
  const settled = saves[0];

  if (settled?.state === 'refused')
    return {
      message: `Not saved — ${settled.reason ?? 'the API refused the change'}`,
      severity: 'error',
    };

  if (unsaved > 0)
    return {
      message: `${plural(unsaved)} — leave the field to save`,
      severity: 'warning',
    };

  return {
    message: settled === undefined ? 'No changes' : 'Saved',
    severity: 'success',
  };
}
